import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { FileText, Upload, Download } from "lucide-react";
import { Card, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CustomerProjectDocuments = () => {
  const [searchParams] = useSearchParams();
  const projectId = searchParams.get("projectId") || "";

  const [projects, setProjects] = useState([]);
  const [selectedProjectId, setSelectedProjectId] = useState(projectId);
  const [documents, setDocuments] = useState([]);
  const [file, setFile] = useState(null);
  const [description, setDescription] = useState("");
  const [loading, setLoading] = useState(true);

  const selectedProject = useMemo(
    () => projects.find((p) => p.id === selectedProjectId),
    [projects, selectedProjectId]
  );

  useEffect(() => {
    const init = async () => {
      try {
        const res = await fetch(`${API}/customer/projects`, { credentials: "include" });
        if (res.ok) {
          const data = await res.json();
          const list = data?.projects ?? data ?? [];
          setProjects(list);
          if (!selectedProjectId && list.length) setSelectedProjectId(list[0].id);
        } else {
          setProjects([]);
        }
      } catch (e) {
        toast.error("Failed to load projects");
        setProjects([]);
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [selectedProjectId]);

  useEffect(() => {
    if (!selectedProjectId) return;

    const fetchDocs = async () => {
      try {
        const res = await fetch(`${API}/customer/projects/${selectedProjectId}/documents`, {
          credentials: "include",
        });
        if (res.ok) {
          const data = await res.json();
          setDocuments(data?.documents ?? data ?? []);
        } else {
          setDocuments([]);
        }
      } catch (e) {
        toast.error("Failed to load documents");
        setDocuments([]);
      }
    };

    fetchDocs();
  }, [selectedProjectId]);

  const handleUpload = async () => {
    if (!selectedProjectId) {
      toast.error("Select a project");
      return;
    }
    if (!file) {
      toast.error("Choose a file");
      return;
    }

    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("description", description);

      const res = await fetch(`${API}/customer/projects/${selectedProjectId}/documents`, {
        method: "POST",
        credentials: "include",
        body: fd,
      });

      if (res.ok) {
        toast.success("Uploaded");
        setFile(null);
        setDescription("");
        const refreshed = await fetch(`${API}/customer/projects/${selectedProjectId}/documents`, {
          credentials: "include",
        });
        if (refreshed.ok) setDocuments(await refreshed.json());
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || "Upload failed");
      }
    } catch (e) {
      toast.error("Upload failed");
    }
  };

  const handleDownload = (doc) => {
    window.open(`${API}/customer/documents/${doc.id}/download`, "_blank");
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="w-12 h-12 border-4 border-[#D4AF37] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="customer-project-documents">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-[#FAFAFA]">Project Files</h1>
        <p className="text-[#A1A1AA] mt-1">Upload reference photos and download deliverables per project</p>
      </div>

      <Card className="bg-[#121214] border-[#27272A]">
        <CardContent className="pt-6 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-[#A1A1AA]">Project</Label>
              <select
                value={selectedProjectId}
                onChange={(e) => setSelectedProjectId(e.target.value)}
                className="mt-1 w-full bg-[#1A1A1D] border border-[#27272A] text-[#FAFAFA] rounded-md px-3 py-2"
                data-testid="project-select"
              >
                {(projects || []).map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.title}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <Label className="text-[#A1A1AA]">Upload</Label>
              <Input
                type="file"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="mt-1 bg-[#1A1A1D] border-[#27272A] text-[#FAFAFA]"
                data-testid="file-input"
              />
            </div>
          </div>

          <div>
            <Label className="text-[#A1A1AA]">Description (optional)</Label>
            <Input
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="e.g., reference photos for lighting"
              className="mt-1 bg-[#1A1A1D] border-[#27272A] text-[#FAFAFA]"
              data-testid="file-description"
            />
          </div>

          <Button className="btn-gold" onClick={handleUpload} data-testid="upload-file-btn">
            <Upload size={16} className="mr-2" />
            Upload to {selectedProject?.title || "project"}
          </Button>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {(documents || []).map((doc) => (
          <Card key={doc.id} className="bg-[#121214] border-[#27272A]" data-testid={`project-doc-${doc.id}`}>
            <CardContent className="pt-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-lg bg-[#D4AF37]/10 flex items-center justify-center flex-shrink-0">
                  <FileText className="text-[#D4AF37]" size={24} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-[#FAFAFA] truncate">{doc.name}</p>
                  {doc.description && <p className="text-sm text-[#71717A] mt-1">{doc.description}</p>}
                  <p className="text-xs text-[#71717A] mt-2">Uploaded by you</p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDownload(doc)}
                  className="text-[#D4AF37]"
                  data-testid={`download-${doc.id}`}
                >
                  <Download size={16} />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}

        {(documents || []).length === 0 && (
          <Card className="col-span-full bg-[#121214] border-[#27272A]">
            <CardContent className="py-12 text-center">
              <FileText size={48} className="text-[#71717A] mx-auto mb-3" />
              <p className="text-[#FAFAFA] font-medium">No files yet</p>
              <p className="text-[#71717A]">Upload a reference file to get started.</p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default CustomerProjectDocuments;
